import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CategoryKey, DiagnosticAnswer, DiagnosticScaleValue, LeadIdentification, Consents } from '@/domains/assessment/types';
import { DIAGNOSTIC_CATEGORIES, TOTAL_QUESTIONS_COUNT, SCALE_LABELS } from '@/domains/assessment/constants';
import type { PublicDiagnosticResult } from '@/domains/results/types';
import { MOCK_SAMPLE_RESULT } from '@/mocks/mockData';
import { diagnosticApi } from '@/services/diagnosticApi';
import { getPersistedUtmParams } from '@/domains/analytics/utm';

interface DiagnosticContextType {
  currentQuestionIndex: number; // 0 a 17 (18 perguntas)
  answers: DiagnosticAnswer[];
  leadInfo: LeadIdentification;
  consents: Consents;
  turnstileToken: string;
  setTurnstileToken: (token: string) => void;
  honeypot: string;
  setHoneypot: (val: string) => void;
  result: PublicDiagnosticResult | null;
  setResult: (result: PublicDiagnosticResult | null) => void;
  setAnswer: (categoryKey: CategoryKey, questionIndex: number, value: DiagnosticScaleValue) => void;
  getAnswerForCurrentQuestion: () => DiagnosticScaleValue | null;
  nextQuestion: () => boolean; // retorna true se avançou, false se era a última
  prevQuestion: () => boolean; // retorna true se recuou
  setLeadInfo: (info: Partial<LeadIdentification>) => void;
  setConsents: (consents: Partial<Consents>) => void;
  processSubmission: (honeypot?: string) => Promise<PublicDiagnosticResult>;
  resetDiagnostic: () => void;
  flatQuestions: {
    globalIndex: number;
    categoryKey: CategoryKey;
    categoryName: string;
    questionIndex: number;
    text: string;
    options: Record<DiagnosticScaleValue, string>;
  }[];
}

const STORAGE_KEY = 'diag_assessment_state';

// Mapeamento plano das 18 perguntas (3 por categoria)
const FLAT_QUESTIONS = DIAGNOSTIC_CATEGORIES.flatMap((category) =>
  category.questions.map((q, questionIndex) => ({
    categoryKey: category.key,
    categoryName: category.name,
    questionIndex,
    text: typeof q === 'string' ? q : q.text,
    options: typeof q === 'string' ? SCALE_LABELS : q.options,
  })),
).map((item, globalIndex) => ({ ...item, globalIndex }));

const DiagnosticContext = createContext<DiagnosticContextType | null>(null);

export function DiagnosticProvider({ children }: { children: ReactNode }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<DiagnosticAnswer[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed.answers || [];
        }
      } catch {
        // fallback
      }
    }
    return [];
  });

  const [leadInfo, setLeadInfoState] = useState<LeadIdentification>({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    companySize: '2 a 5 pessoas',
  });

  const [consents, setConsentsState] = useState<Consents>({
    consentPrivacy: false,
    consentContact: false,
  });

  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [honeypot, setHoneypot] = useState<string>('');

  const [result, setResult] = useState<PublicDiagnosticResult | null>(() => {
    return MOCK_SAMPLE_RESULT; // Pré-populado com dados mockados realistas para prototipação
  });

  // Salva respostas parciais no sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, currentQuestionIndex }));
    } catch {
      // Ignora erro de cota
    }
  }, [answers, currentQuestionIndex]);

  const setAnswer = (categoryKey: CategoryKey, questionIndex: number, value: DiagnosticScaleValue) => {
    setAnswers((prev) => {
      const filtered = prev.filter(
        (a) => !(a.categoryKey === categoryKey && a.questionIndex === questionIndex),
      );
      return [...filtered, { categoryKey, questionIndex, value }];
    });
  };

  const getAnswerForCurrentQuestion = (): DiagnosticScaleValue | null => {
    const currentQ = FLAT_QUESTIONS[currentQuestionIndex];
    if (!currentQ) return null;
    const found = answers.find(
      (a) => a.categoryKey === currentQ.categoryKey && a.questionIndex === currentQ.questionIndex,
    );
    return found ? found.value : null;
  };

  const nextQuestion = (): boolean => {
    if (currentQuestionIndex < TOTAL_QUESTIONS_COUNT - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      return true;
    }
    return false;
  };

  const prevQuestion = (): boolean => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      return true;
    }
    return false;
  };

  const setLeadInfo = (info: Partial<LeadIdentification>) => {
    setLeadInfoState((prev) => ({ ...prev, ...info }));
  };

  const setConsents = (newConsents: Partial<Consents>) => {
    setConsentsState((prev) => ({ ...prev, ...newConsents }));
  };

  // ==============================================================================
  // TEMPORARY EVENT MVP MODE (Ativo para o evento presencial)
  // ==============================================================================
  // - Exigência do Turnstile desativada temporariamente no frontend e backend
  // - Proteção assegurada por honeypot invisível, validação de payload e rate limit
  // ==============================================================================
  const EVENT_MVP_MODE = true;

  const processSubmission = async (honeypot?: string): Promise<PublicDiagnosticResult> => {
    const isProduction = import.meta.env.VITE_APP_ENV === 'production';
    const trimmedToken = turnstileToken.trim();

    // Em modo normal de produção, exige token real do Turnstile; em EVENT_MVP_MODE, bypassa
    if (!EVENT_MVP_MODE && isProduction && (!trimmedToken || trimmedToken === '1x00000000000000000000AA')) {
      throw new Error(
        'Validação de segurança anti-bot (Cloudflare Turnstile) obrigatória em produção.',
      );
    }

    const utms = getPersistedUtmParams();
    const tokenToSend = trimmedToken || 'event_mvp_mode_token';

    // Limpa imediatamente o token do estado para garantir que seja estritamente de uso único
    setTurnstileToken('');

    const payload = {
      answers: answers.map((a) => ({
        categoryKey: a.categoryKey,
        questionIndex: a.questionIndex,
        scaleValue: a.value,
      })),
      lead: leadInfo,
      consents: {
        consentOperational: consents.consentPrivacy,
        consentCommercial: consents.consentContact,
        consentVersion: 'v1.0',
        consentOrigin: 'web_form',
      },
      utms,
      turnstileToken: tokenToSend,
      honeypot: honeypot || undefined,
    };

    const computedResult = await diagnosticApi.submitDiagnostic(payload);
    setResult(computedResult);
    return computedResult;
  };

  const resetDiagnostic = () => {
    setAnswers([]);
    setCurrentQuestionIndex(0);
    setLeadInfoState({
      name: '',
      email: '',
      phone: '',
      companyName: '',
      companySize: '2 a 5 pessoas',
    });
    setConsentsState({ consentPrivacy: false, consentContact: false });
    setTurnstileToken('');
    setHoneypot('');
    setResult(null);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <DiagnosticContext.Provider
      value={{
        currentQuestionIndex,
        answers,
        leadInfo,
        consents,
        turnstileToken,
        setTurnstileToken,
        honeypot,
        setHoneypot,
        result,
        setResult,
        setAnswer,
        getAnswerForCurrentQuestion,
        nextQuestion,
        prevQuestion,
        setLeadInfo,
        setConsents,
        processSubmission,
        resetDiagnostic,
        flatQuestions: FLAT_QUESTIONS,
      }}
    >
      {children}
    </DiagnosticContext.Provider>
  );
}

export function useDiagnostic() {
  const context = useContext(DiagnosticContext);
  if (!context) {
    throw new Error('useDiagnostic deve ser usado dentro de um DiagnosticProvider');
  }
  return context;
}
